import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GoogleMap } from '@angular/google-maps';
import { Subject, takeUntil } from 'rxjs';
import { Order } from 'src/app/models/order';
import { orderStatusKeysConst } from 'src/app/core/const/orderStatusKey.const';
import { SocketService } from 'src/app/core/services/socket.service';
import { InitialService } from 'src/app/core/services/initial.service';

interface DriverLocation {
  latitude: number;
  longitude: number;
}

@Component({
  selector: 'megastore-admin-driver-tracking',
  templateUrl: './driver-tracking.component.html',
  styleUrls: ['./driver-tracking.component.scss']
})
export class DriverTrackingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;

  orderStatus = orderStatusKeysConst;
  destroy$ = new Subject<void>();
  
  // Map configuration
  mapCenter: google.maps.LatLngLiteral = {
    lat: 30.7073833,
    lng: 76.7199324
  };
  
  options: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 8,
    zoom: 13
  };

  // Markers
  driverMarker: google.maps.LatLngLiteral | null = null;
  dropoffMarker: google.maps.LatLngLiteral | null = null;
  driverMarkerOptions: google.maps.MarkerOptions = {};
  dropoffMarkerOptions: google.maps.MarkerOptions = {};
  
  // Route
  routePath: google.maps.LatLngLiteral[] = [];
  directionsService = new google.maps.DirectionsService();
  directionsRenderer = new google.maps.DirectionsRenderer();
  
  // Tracking state
  isTracking = false;
  currentOrderStatus: number;
  dropoffLocation: google.maps.LatLngLiteral | null = null;
  driverLocation: { lat: any; lng: any; } | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { order: Order },
    private socketService: SocketService,
    private dialogRef: MatDialogRef<DriverTrackingComponent>,
    private initialService: InitialService
  ) {
    this.currentOrderStatus = this.data.order.status;
    this.initializeDropoffLocation();
  }

  private initializeDropoffLocation(): void {
    const address = this.data.order.deliveryAddress;
    if (address && (address as any).latitude && (address as any).longitude) {
      this.dropoffLocation = {
        lat: (address as any).latitude,
        lng: (address as any).longitude
      };
      this.dropoffMarker = this.dropoffLocation;
      this.mapCenter = this.dropoffLocation;

      if (this.data.order.assignedDriver && this.data.order.assignedDriver.latitude && this.data.order.assignedDriver.longitude) {
        this.driverLocation = {
          lat: this.data.order.assignedDriver.latitude,
          lng: this.data.order.assignedDriver.longitude
        };
        this.driverMarker = this.driverLocation;
        this.mapCenter = this.driverLocation;

        if (this.driverLocation) {
          this.calculateRoute(this.driverLocation, this.dropoffLocation);
        }
      }
    }
  }

  async ngOnInit(): Promise<void> {
    // Ensure Google Maps is loaded
    await this.initialService.setUpGoogleScript();
    
    // Initialize marker options after Google Maps is loaded
    if (typeof google !== 'undefined' && google.maps) {
      this.driverMarkerOptions = {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      };
      this.dropoffMarkerOptions = {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#34A853',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      };
    }
    
    // Check if tracking should be enabled
    const trackingStatuses = [
      this.orderStatus.ORDER_PICKED_UP,
      this.orderStatus.ON_THE_WAY,
      this.orderStatus.DELIVERED
    ];
    
    if (trackingStatuses.includes(this.currentOrderStatus)) {
      this.socketService.initSocket();
      this.startTracking();
    }
  }

  ngAfterViewInit(): void {
    // Wait for Google Maps to be fully loaded
    setTimeout(() => {
      if (this.map?.googleMap) {
        this.directionsRenderer.setMap(this.map.googleMap);
        this.directionsRenderer.setOptions({
          suppressMarkers: true, // We'll use custom markers
          polylineOptions: {
            strokeColor: '#4285F4',
            strokeWeight: 4
          }
        });
      }
    }, 100);
  }

  startTracking(): void {
    if (this.currentOrderStatus === this.orderStatus.DELIVERED) {
      return; // Don't start tracking if already delivered
    }

    this.isTracking = true;
    
    // Join the room for this order
    this.socketService.emitAction('joinRoom', {
      orderId: this.data.order._id
    });

    // Listen for join room acknowledgment
    this.socketService
      .onEvent('joinRoomOk')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          console.log('Joined room:', data);
        }
      });

    // Listen for driver location updates
    this.socketService
      .onEvent('updateLocationOk')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: DriverLocation) => {
          if (data && data.latitude && data.longitude) {
            this.updateDriverLocation({
              lat: data.latitude,
              lng: data.longitude
            });
          }
        }
      });

    // Listen for status changes
    this.socketService
      .onEvent('statusChangeOk')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (data && data.status !== undefined) {
            this.handleStatusChange(data.status);
          }
        }
      });

    // Listen for order status updates
    this.socketService
      .onEvent('updatedOrderStatus')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedOrder: Order) => {
          if (updatedOrder._id === this.data.order._id) {
            this.handleStatusChange(updatedOrder.status);
          }
        }
      });
  }

  private updateDriverLocation(location: google.maps.LatLngLiteral): void {
    this.driverMarker = location;
    
    // Update map center to show both driver and dropoff
    if (this.dropoffLocation) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(location);
      bounds.extend(this.dropoffLocation);
      
      if (this.map?.googleMap) {
        this.map.googleMap.fitBounds(bounds);
      }
    } else {
      this.mapCenter = location;
    }

    // Update route if dropoff location exists
    if (this.dropoffLocation) {
      this.calculateRoute(location, this.dropoffLocation);
    }
  }

  private calculateRoute(
    origin: google.maps.LatLngLiteral,
    destination: google.maps.LatLngLiteral
  ): void {
    if (!this.directionsService) {
      return;
    }

    this.directionsService.route(
      {
        origin: origin,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result && result.routes.length > 0) {
          const route = result.routes[0];
          const path: google.maps.LatLngLiteral[] = [];
          
          route.legs.forEach(leg => {
            leg.steps.forEach(step => {
              step.path.forEach(point => {
                path.push({
                  lat: point.lat(),
                  lng: point.lng()
                });
              });
            });
          });
          
          this.routePath = path;
          console.log('routePath', this.routePath);
          // Update directions renderer if map is available
          if (this.map?.googleMap && this.directionsRenderer) {
            this.directionsRenderer.setDirections(result);
          }
        } else {
          console.error('Directions request failed:', status);
        }
      }
    );
  }

  private handleStatusChange(newStatus: number): void {
    this.currentOrderStatus = newStatus;
    
    // Stop tracking if delivered
    if (newStatus === this.orderStatus.DELIVERED) {
      this.stopTracking();
    }
  }

  private stopTracking(): void {
    this.isTracking = false;
    // Leave the room
    this.socketService.emitAction('leaveRoom', {
      orderId: this.data.order._id
    });
  }

  close(): void {
    this.stopTracking();
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.stopTracking();
    this.destroy$.next();
    this.destroy$.complete();
  }
}

